import DataTable from 'Components/DataTable';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { isLocaleKey, mgQol15r } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { MyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import { Theme, useMediaQuery } from '@mui/material';

const mgQol15rFields: Array<keyof IMGQoL15r> = [
  'frustrated',
  'troubleUsingEyes',
  'troubleEating',
  'limitedSocialActivity',
  'limitEnjoyment',
  'troubleMeetingNeeds',
  'plansAroundMG',
  'bothered',
  'difficultySpeaking',
  'lostPersonalIndependence',
  'depressed',
  'troubleWalking',
  'troubleGettingAround',
  'overwhelmed',
  'troubleWithPersonalGrooming',
];

export const MGQol15r = (): React.JSX.Element => {
  const myServContext = React.useContext(MyServiceContext);
  const { editing, setEditingData, locale, viewing } = myServContext;
  const mgQol15rData = (editing?.data || viewing?.data) as IMGQoL15r & IControlProps;
  const mgQol15rLocales = mgQol15r.localizations;
  const useLocale = isLocaleKey(locale) ? locale : 'fi';
  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    const value = Object.values(values)[0];
    setEditingData?.({
      ...mgQol15rData,
      [field]: field !== 'date' && value && typeof value === 'string' ? parseInt(value) : value,
    });
  };

  // Track page width and breakpoints
  const mdOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down(1401));

  const totalScore = mgQol15r.calculators.calculateMGQoL15rTotalScore(mgQol15rData ?? {});

  return (
    <>
      <BlockWrapper title={viewing ? formatPartialDate(mgQol15rData.date) : undefined}>
        {!viewing && (
          <FormRow title={'general.date'}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: mgQol15rData?.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        {!viewing && (
          <div>
            <p style={{ color: colors.tertiaryText, fontSize: '2rem', fontWeight: 600, margin: 0 }}>
              {mgQol15rLocales[useLocale]['stepInfo']}
            </p>
            <p style={{ color: colors.tertiaryText, margin: 0, marginBottom: editing ? '2rem' : 0 }}>
              {mgQol15rLocales[useLocale]['stepInfoDesc']}
            </p>
          </div>
        )}
        <div
          style={{
            width: mdOrSmaller ? '100%' : '70%',
            marginBottom: '2rem',
            marginTop: viewing ? '6rem' : undefined,
          }}
        >
          <DataTable
            headers={['', '']}
            data={{
              rowData: mgQol15rFields.map((f, i) => {
                const rowSet = [
                  `${i + 1}. ` + mgQol15rLocales[useLocale][f],
                  <div key={f} style={viewing ? { width: '15rem' } : { width: '20rem' }}>
                    <InputHandler
                      name={f}
                      type="Radio"
                      editing={!!editing}
                      formData={{
                        document: { [f]: mgQol15rData?.[f] },
                        onChange: onChangeSurveyForm,
                      }}
                      options={[0, 1, 2]}
                      optionFormatter={o => `${o} - ` + mgQol15rLocales[useLocale][`opts.${o}`]}
                    />
                  </div>,
                ];
                return rowSet;
              }),
              rowStyle: viewing ? undefined : mgQol15rFields.map(() => ({ rowAlign: 'top' })),
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: viewing ? '62%' : '60%',
            color: colors.tertiaryText,
            marginBottom: '0.5rem',
          }}
        >
          <span style={{ fontWeight: 600 }}>{mgQol15rLocales[useLocale]['totalScore']}</span>
          <span
            style={{
              fontWeight: 600,
              width: viewing ? '5.5rem' : '16rem',
            }}
          >
            <span>{totalScore || totalScore === 0 ? totalScore : '-'}</span>
          </span>
        </div>
      </BlockWrapper>
      <div style={{ color: colors.tertiaryText }}>
        <p>{mgQol15rLocales[useLocale]['reference']}</p>
      </div>
    </>
  );
};
